// Income/expense name suggestions — now a unified {name, price, category} model.
//
// A "suggestion" is a service the shop earns from (income) or spends on (expense).
// Each carries an optional price. Income additionally includes the 5 built-in
// MODULES (image gen, doc gen, AI template, manual edit, print media): these are
// always present and not deletable. Their price is editable, and the user may RENAME
// them (the saved entry's name wins over the default below); that name + price is what
// the auto-popup fills when the matching task completes.
//
// Three tiers the user sees in the income list:
//   1. modules      — predefined, priced, not deletable, auto-triggered (category set)
//   2. predefined   — a few common names WITHOUT a price (user fills it), category null
//   3. custom       — user-added name + price, category null
//
// Storage: settings.incomeSuggestions / settings.expenseSuggestions. Entries may be
// legacy plain strings (old accounts) or {name, price, category} objects — the
// normalizer accepts BOTH so nothing an existing user saved is ever lost.

// ── Built-in income modules ────────────────────────────────────────────────
// name       — fixed display/service name (also the auto-popup entry name)
// fallback   — price used only when neither a saved entry nor the legacy
//              settings.<legacyAmountKey> exists (matches the old code defaults)
// legacyKey  — the pre-unification per-module amount field to migrate from
// Names mirror the main header navbar (src/constants/menuItems.jsx → navigation.*).
export const INCOME_MODULES = [
    { category: 'image_generation', name: 'এআই এডিটর',       fallback: 60, legacyKey: 'defaultImageGenAmount' },
    { category: 'manual_edit',      name: 'ম্যানুয়াল এডিটর',  fallback: 50, legacyKey: 'defaultManualEditAmount' },
    { category: 'bulk_photo',       name: 'বাল্ক ফটো এডিট',   fallback: 50, legacyKey: 'defaultBulkPhotoAmount' },
    { category: 'ai_template',      name: 'এআই টেমপ্লেট',      fallback: 20, legacyKey: 'defaultAiTemplateAmount' },
    { category: 'question_paper',   name: 'প্রশ্নপত্র',         fallback: 30, legacyKey: 'defaultQuestionPaperAmount' },
    { category: 'doc_generation',   name: 'ডকুমেন্ট জেনারেট',   fallback: 40, legacyKey: 'defaultDocGenAmount' },
    { category: 'print_media',      name: 'প্রিন্ট মিডিয়া',    fallback: 30, legacyKey: 'defaultPrintMediaAmount' },
];
const MODULE_BY_CATEGORY = new Map(INCOME_MODULES.map((m) => [m.category, m]));

// Priceless predefined income services shown to brand-new accounts (user fills price).
export const DEFAULT_INCOME_EXTRAS = ['প্রিন্ট', 'লেমিনেশন', 'ফটোকপি'];

// ── Common service-name quick picks (income) ───────────────────────────────
// The full "আধুনিক কম্পিউটার দোকানের রেট চার্ট" — the 60 services a Bangladeshi
// computer/online-service shop typically offers. Shown as tap-to-add name chips in
// Settings. NAMES ONLY (no price) — prices vary per shop / are ranges, so the
// shopkeeper types the amount each time. Reference catalog (data), like
// INCOME_MODULES — not translated via t(). Kept in rate-card order.
export const COMMON_INCOME_SERVICES = [
    'অনলাইন ফরম ফিলাপ',
    'টেলিটক / কেজিএস ফরম ফিলাপ',
    'NID অনলাইন কপি',
    'NID কার্ড ডাউনলোড + প্রিন্ট + লেমিনেট',
    'জন্মনিবন্ধন অনলাইন কপি (কালার প্রিন্ট)',
    'সাদা-কালো প্রিন্ট',
    'নতুন জন্মনিবন্ধন আবেদন',
    'জন্মনিবন্ধন সংশোধন আবেদন',
    'আইডি কার্ড স্ক্যান + প্রিন্ট + লেমিনেট',
    'বাংলা / ইংরেজি টাইপিং',
    'CV তৈরি',
    'বাংলা টাইপ + প্রিন্ট',
    'ডিজাইনসহ টাইপিং',
    'PDF সাদা-কালো প্রিন্ট',
    'কালার প্রিন্ট',
    'হোয়াটসঅ্যাপ / মোবাইল ফাইল প্রিন্ট',
    'ফুল ফটো পেপার প্রিন্ট',
    'পাসপোর্ট সাইজ ছবি প্রিন্ট',
    'ল্যাব প্রিন্ট / গ্লসি প্রিন্ট',
    'আইডি কার্ড লেমিনেশন',
    'A4 লেমিনেশন',
    'ই-মেইল আদান প্রদান / ডকুমেন্ট সেন্ড',
    'ভিসা চেক + প্রিন্ট',
    'BMET আবেদন',
    'BMET কার্ড ডাউনলোড + কালার প্রিন্ট',
    'সরকারি আবেদন / অনুদান আবেদন',
    'পাসপোর্ট চেক',
    'পুলিশ ক্লিয়ারেন্স / ট্র্যাকিং কার্ড আবেদন',
    'অ্যাপয়েন্টমেন্ট লেটার / বুকিং',
    'E-TIN সার্টিফিকেট',
    'বায়োমেট্রিক ফিঙ্গার চেক + প্রিন্ট',
    'বিমানের টিকিট চেক',
    'অনলাইন টিকিট বুকিং',
    'মোবাইল ছবি প্রিন্ট',
    'ফটোকপি',
    'স্ক্যান',
    'পাসপোর্ট ছবি এডিট',
    'ইউটিউব থাম্বনেইল ডিজাইন',
    'ফেসবুক পোস্টার / ব্যানার ডিজাইন',
    'কম্পিউটার কম্পোজ + প্রিন্ট',
    'NID সংশোধন আবেদন',
    'স্মার্ট কার্ড স্ট্যাটাস চেক',
    'ড্রাইভিং লাইসেন্স আবেদন',
    'আয়কর রিটার্ন সাবমিট',
    'টিন সার্টিফিকেট সংশোধন',
    'জমির খতিয়ান / পর্চা ডাউনলোড',
    'নামজারি আবেদন',
    'বিশ্ববিদ্যালয় ভর্তি আবেদন',
    'চাকরির আবেদন',
    'অনলাইন রেজিস্ট্রেশন',
    'ফেসবুক / ইউটিউব খুলে দেওয়া',
    'Gmail / Email তৈরি',
    'মোবাইল সফটওয়্যার / ফ্ল্যাশ',
    'ছবি ব্যাকগ্রাউন্ড রিমুভ',
    'স্ট্যাম্প / সিল ডিজাইন',
    'বিজনেস কার্ড ডিজাইন',
    'লোগো ডিজাইন',
    'ফ্লেক্সিলোড / মোবাইল ব্যাংকিং',
    'ফর্ম প্রিন্ট + পূরণ',
    'কালার ফটোকপি',
];

// Expense (ব্যয়) defaults — the things a shop typically spends on. No modules.
export const DEFAULT_EXPENSE_SUGGESTIONS = ['কাগজ', 'কালি', 'বিদ্যুৎ বিল', 'ইন্টারনেট বিল'];

// ── Backward-compat aliases (old single-list / name-only API) ───────────────
export const DEFAULT_INCOME_SUGGESTIONS = ['প্রিন্ট', 'ফটো এডিট', 'ডকুমেন্ট জেনারেট'];
export const DEFAULT_INCOME_EXPENSE_SUGGESTIONS = DEFAULT_INCOME_SUGGESTIONS;
export const DEFAULT_ACTIVE_INCOME_SUGGESTION = 'ইমেজ জেনারেট';
export const DEFAULT_ACTIVE_EXPENSE_SUGGESTION = 'কাগজ';
export const DEFAULT_ACTIVE_SUGGESTION = DEFAULT_ACTIVE_INCOME_SUGGESTION;

/** The suggestion NAME pre-selected in the modal for a given entry type. */
export function defaultActiveSuggestionForType(type) {
    return type === 'expense' ? DEFAULT_ACTIVE_EXPENSE_SUGGESTION : DEFAULT_ACTIVE_INCOME_SUGGESTION;
}

/** Coerce a stored suggestion (legacy string OR object) to {name, price, category}
 *  or null when it has no usable name. price is a Number or null (0 is kept). */
export function normalizeSuggestion(s) {
    if (s == null) return null;
    if (typeof s === 'string') {
        const name = s.normalize('NFC').trim();
        return name ? { name, price: null, category: null } : null;
    }
    if (typeof s === 'object') {
        const name = String(s.name ?? '').normalize('NFC').trim();
        if (!name) return null;
        const priceOk = s.price === 0 || (s.price != null && s.price !== '' && !Number.isNaN(Number(s.price)));
        return { name, price: priceOk ? Number(s.price) : null, category: s.category || null };
    }
    return null;
}

/** Read the display name from a suggestion that may be a string or an object. */
export function suggestionName(s) {
    return typeof s === 'string' ? s : (s?.name ?? '');
}

/** The per-type saved list (normalized objects), if the user ever saved one; else null. */
function savedSuggestionsForType(settings, type) {
    const raw = type === 'income' ? settings?.incomeSuggestions : settings?.expenseSuggestions;
    if (!Array.isArray(raw)) return null;
    return raw.map(normalizeSuggestion).filter(Boolean);
}

/** Dedupe by normalized name (first occurrence wins). */
function dedupeByName(list) {
    const seen = new Set();
    const out = [];
    for (const s of list) {
        const k = s.name.normalize('NFC').trim().toLowerCase();
        if (!k || seen.has(k)) continue;
        seen.add(k);
        out.push(s);
    }
    return out;
}

/** Resolve every module's live price: a saved entry's price wins, else the legacy
 *  per-module amount, else the module's hardcoded fallback. The name is the user's
 *  saved rename, else the module's default. */
function moduleEntries(settings, savedByCategory) {
    return INCOME_MODULES.map((m) => {
        const saved = savedByCategory.get(m.category);
        // A renamed module keeps its saved name; otherwise the built-in default.
        const name = saved?.name || m.name;
        if (saved && saved.price != null) return { name, price: saved.price, category: m.category };
        const legacy = settings?.[m.legacyKey];
        const price = legacy != null && legacy !== '' && !Number.isNaN(Number(legacy)) ? Number(legacy) : m.fallback;
        return { name, price, category: m.category };
    });
}

/**
 * Full ordered suggestion list for a type as {name, price, category} objects.
 *
 * INCOME: the 5 modules always lead (fixed name, resolved price), followed by the
 * user's non-module services (saved list is authoritative; else priceless defaults
 * for a new account; else legacy extras merged onto defaults).
 * EXPENSE: no modules — saved list, else legacy merge, else defaults.
 */
export function buildSuggestionsForType(settings, type) {
    const saved = savedSuggestionsForType(settings, type);

    if (type === 'income') {
        const savedByCategory = new Map((saved || []).filter((s) => s.category).map((s) => [s.category, s]));
        const modules = moduleEntries(settings, savedByCategory);
        let extras;
        if (saved) {
            extras = saved.filter((s) => !s.category); // authoritative user list (modules stripped)
        } else if (Array.isArray(settings?.incomeExpenseSuggestions)) {
            extras = [...DEFAULT_INCOME_EXTRAS, ...settings.incomeExpenseSuggestions].map(normalizeSuggestion).filter(Boolean);
        } else {
            extras = DEFAULT_INCOME_EXTRAS.map(normalizeSuggestion).filter(Boolean);
        }
        return dedupeByName([...modules, ...extras]);
    }

    let source;
    if (saved) source = saved;
    else if (Array.isArray(settings?.incomeExpenseSuggestions)) {
        source = [...DEFAULT_EXPENSE_SUGGESTIONS, ...settings.incomeExpenseSuggestions].map(normalizeSuggestion).filter(Boolean);
    } else {
        source = DEFAULT_EXPENSE_SUGGESTIONS.map(normalizeSuggestion).filter(Boolean);
    }
    return dedupeByName(source);
}

/**
 * The {name, price} the auto-popup should use for a completed module task.
 * Always resolves (modules are always in the income list); the extra fallbacks
 * guard against an unknown category. `price` may be null (no preset price).
 */
export function moduleAutoFill(settings, category) {
    const entry = buildSuggestionsForType(settings, 'income').find((s) => s.category === category);
    if (entry) return { name: entry.name, price: entry.price };
    const m = MODULE_BY_CATEGORY.get(category);
    const legacy = m ? settings?.[m.legacyKey] : null;
    return {
        name: m?.name || '',
        price: legacy != null && legacy !== '' && !Number.isNaN(Number(legacy)) ? Number(legacy) : (m?.fallback ?? null),
    };
}

/**
 * The full income-service list a shop offers, as SettingsPage renders it:
 * the AUTO modules first (with resolved prices), then the shop's own
 * businessProfile.services (with their saved price, else null). Deduped by name.
 * → [{ name, price, category }]  (category set = auto module; null = shop service)
 */
export function buildShopServiceList(settings, businessProfile) {
    const built = buildSuggestionsForType(settings, 'income');
    const norm = (s) => (s || '').normalize('NFC').trim().toLowerCase();
    const modules = built.filter((s) => s.category);
    const priceByName = new Map(built.filter((s) => !s.category).map((s) => [norm(s.name), s.price]));
    const shopServices = Array.isArray(businessProfile?.services) ? businessProfile.services : [];
    const list = [...modules];
    for (const svc of shopServices) {
        const nm = (svc || '').normalize('NFC').trim();
        const key = norm(nm);
        if (nm && !list.some((s) => norm(s.name) === key)) {
            list.push({ name: nm, price: priceByName.has(key) ? priceByName.get(key) : null, category: null });
        }
    }
    return list;
}
